/**
 * THE AD A VISIT CAME FROM, KEPT FOR THE VISIT AND FOR A WEEK.
 *
 * `utils/adLanding.js` records an ad landing once (POST /ads/click) under a click id the browser
 * makes up. What the shopper then does in the shop is sent as journey steps (utils/shopJourney.js),
 * and each step names that click, so the server can tie the steps to the ad that brought them. So
 * the click is remembered: in sessionStorage for the visit it began, and in localStorage for seven
 * days, so a shopper who comes back tomorrow — by a bookmark, a WhatsApp link, a new tab — still
 * counts as the ad's.
 *
 * What is kept is the click's id, the campaign and exact ad the landing named, the platform's own
 * click id (to know the same click landing again), and the business it was a landing on (its id,
 * and its slug to recognise its pages on stylon.online). No name, no phone, nothing the shopper
 * typed — and none of it but the click id and the campaign ever leaves in a step.
 *
 * Storage can be missing or refuse (private windows, blocked site data): every read answers null
 * then and every write is skipped. Nothing here throws.
 */

const VISIT_KEY = 'stylon_ad_visit';
/** A return visit counts for the ad for this long after the click. */
export const AD_VISIT_DAYS = 7;
const AD_VISIT_MS = AD_VISIT_DAYS * 24 * 60 * 60 * 1000;

const listeners = new Set();
/** The click this page session remembered — kept in memory too, for a browser whose storage refused it. */
let rememberedHere = null;

/**
 * The ad a landing address names, or null: our campaign (`stylon_cid`), the platform's own id for
 * the exact ad (`stylon_ad`, digits — an unfilled template tag or anything else is not an ad), and
 * the platform's click id (`gclid` / `fbclid`).
 */
export function adOfLocation(search) {
  try {
    const params = new URLSearchParams(search || '');
    const campaignId = params.get('stylon_cid') || undefined;
    const platformAdRef = /^\d{5,30}$/.test(params.get('stylon_ad') || '') ? params.get('stylon_ad') : undefined;
    if (!campaignId && !platformAdRef) return null;
    const platformClickId = params.get('gclid') || params.get('fbclid') || undefined;
    return { campaignId, platformAdRef, platformClickId };
  } catch (_) {
    return null;
  }
}

/** Whether a remembered visit is the click of the ad a landing names — the same rule the click's de-dupe keys follow. */
export function isVisitOfAd(visit, ad) {
  if (!visit || !ad) return false;
  return (!!ad.campaignId && ad.campaignId === visit.campaignId)
    || (!!ad.platformAdRef && ad.platformAdRef === visit.platformAdRef);
}

function read(storage) {
  try {
    const raw = storage && storage.getItem(VISIT_KEY);
    const visit = raw ? JSON.parse(raw) : null;
    return visit && typeof visit.clickId === 'string' && typeof visit.at === 'number' ? visit : null;
  } catch (_) {
    return null;
  }
}

function write(storage, visit) {
  try {
    if (storage) storage.setItem(VISIT_KEY, JSON.stringify(visit));
  } catch (_) { /* storage refused: the visit simply is not kept */ }
}

const sessionStore = () => (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
const localStore = () => (typeof localStorage !== 'undefined' ? localStorage : null);

/** Keep `{ clickId, campaignId, platformAdRef, platformClickId, salonId, slug }` as the ad this visit came from. */
export function rememberAdVisit({
  clickId, campaignId = null, platformAdRef = null, platformClickId = null, salonId, slug = null,
}, now = Date.now()) {
  if (!clickId || !salonId) return;
  const visit = {
    clickId,
    campaignId: campaignId || null,
    platformAdRef: platformAdRef || null,
    platformClickId: platformClickId || null,
    salonId,
    slug: slug || null,
    at: now,
  };
  rememberedHere = visit;
  write(sessionStore(), visit);
  write(localStore(), visit);
  listeners.forEach((listener) => {
    try { listener(visit); } catch (_) { /* a listener's failure is its own */ }
  });
}

/** The ad this visit came from: this visit's click, else one from the last seven days; else null. */
export function currentAdVisit(now = Date.now()) {
  const session = read(sessionStore()) || rememberedHere;
  if (session) return session;
  const kept = read(localStore());
  if (!kept) return null;
  if (now - kept.at > AD_VISIT_MS || now < kept.at - 60 * 1000) return null;
  return kept;
}

/** Tests only: forget the click this page session remembered. */
export function __forgetAdVisitInMemory() {
  rememberedHere = null;
}

/** Be told when a landing's click is remembered. Answers the function that stops it. */
export function onAdVisitRemembered(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
